import React, { useMemo, useState } from "react";
import { Button, Grid, Stack, TextField, Typography } from "@mui/material";

import { PagedTable, SectionCard, formatDate, safeArray, toTitleCase } from "../coreNetworkUiShared";

const emptyForm = {
  slotDate: "",
  capabilityType: "",
  availableUnits: "",
  bookedUnits: "",
  notes: "",
};

export default function CoreNetworkCapacityPanel({
  slots = [],
  onSaveSlot,
  saving = false,
}) {
  const [form, setForm] = useState(emptyForm);

  const sortedSlots = useMemo(
    () => [...safeArray(slots)].sort((a, b) => new Date(a.slotDate || 0) - new Date(b.slotDate || 0)),
    [slots]
  );

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    await onSaveSlot?.({
      slotDate: form.slotDate,
      capabilityType: form.capabilityType || null,
      availableUnits: form.availableUnits ? Number(form.availableUnits) : null,
      bookedUnits: form.bookedUnits ? Number(form.bookedUnits) : 0,
      notes: form.notes || null,
    });
    setForm(emptyForm);
  };

  return (
    <SectionCard title="Capacity" subtitle="Availability snapshots help matching and scheduling make better choices.">
      <PagedTable
        rows={sortedSlots}
        columns={[
          { key: "slotDate", label: "Date", render: (row) => formatDate(row.slotDate) },
          { key: "capabilityType", label: "Capability", render: (row) => toTitleCase(row.capabilityType) },
          { key: "availableUnits", label: "Available", render: (row) => row.availableUnits ?? "-" },
          { key: "bookedUnits", label: "Booked", render: (row) => row.bookedUnits ?? 0 },
          { key: "notes", label: "Notes", render: (row) => row.notes || "-" },
        ]}
        emptyTitle="No capacity slots recorded"
        emptyDescription="Add a capacity slot to start feeding timeline-aware matching."
      />

      {onSaveSlot ? (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Add capacity slot
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="date" label="Slot date" value={form.slotDate} onChange={setField("slotDate")} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Capability type" value={form.capabilityType} onChange={setField("capabilityType")} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Available" value={form.availableUnits} onChange={setField("availableUnits")} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Booked" value={form.bookedUnits} onChange={setField("bookedUnits")} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Notes" value={form.notes} onChange={setField("notes")} />
            </Grid>
          </Grid>
          <div>
            <Button variant="outlined" onClick={handleSave} disabled={saving || !form.slotDate}>
              {saving ? "Saving..." : "Save slot"}
            </Button>
          </div>
        </Stack>
      ) : null}
    </SectionCard>
  );
}
